import { CommunicationChannel } from '@community_dev/types/lib/api/CommunicationChannel'
import { CampaignDisplayStatus } from '@community_dev/types/lib/api/v2/Campaign'
import dayjs from 'dayjs'

import { Tone } from '../../colors'

import { Campaign } from 'api/campaigns'

export const LATEST_CAMPAIGNS_LIMIT = 5
export const LATEST_CAMPAIGNS_MONTHS = 12

export const CAMPAIGN_STATUS_DISPLAY: Record<CampaignDisplayStatus, { label: string; tone: Tone }> = {
  [CampaignDisplayStatus.DRAFTED]: { label: 'Draft', tone: 'neutral' },
  [CampaignDisplayStatus.SCHEDULED]: { label: 'Scheduled', tone: 'neutral' },
  [CampaignDisplayStatus.SENDING]: { label: 'Sending', tone: 'warning' },
  [CampaignDisplayStatus.ACTIVE]: { label: 'Active', tone: 'success' },
  [CampaignDisplayStatus.COMPLETE]: { label: 'Complete', tone: 'success' },
  [CampaignDisplayStatus.FAILED]: { label: 'Failed', tone: 'error' },
  [CampaignDisplayStatus.IN_REVIEW]: { label: 'In Review', tone: 'warning' },
  [CampaignDisplayStatus.NEEDS_APPROVAL]: { label: 'Needs Approval', tone: 'warning' },
}

const SENT_STATUSES: CampaignDisplayStatus[] = [
  CampaignDisplayStatus.SENDING,
  CampaignDisplayStatus.ACTIVE,
  CampaignDisplayStatus.COMPLETE,
]

const percentFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 })

export type LatestCampaign = {
  id: string
  name: string
  channel: CommunicationChannel
  recipients: number
  responseRate: string
  clickRate: string
  stopRate: string
  status: { label: string; tone: Tone }
  dateLabel: 'Created' | 'Sent' | 'Scheduled'
  date: string
}

function toPercent(rate?: number | null): string {
  return rate == null ? '' : `${percentFormatter.format(rate)}%`
}

function toDate(campaign: Campaign): Pick<LatestCampaign, 'dateLabel' | 'date'> {
  if (campaign.displayStatus === CampaignDisplayStatus.DRAFTED) {
    return { dateLabel: 'Created', date: campaign.createdAt }
  }
  if (SENT_STATUSES.includes(campaign.displayStatus)) {
    return { dateLabel: 'Sent', date: campaign.scheduledAt ?? campaign.submittedAt }
  }
  if (campaign.scheduledAt) {
    return { dateLabel: 'Scheduled', date: campaign.scheduledAt }
  }
  return { dateLabel: 'Created', date: campaign.createdAt }
}

function toName(campaign: Campaign): string {
  const name = campaign.name?.trim() || campaign.text?.trim()
  return name || 'Untitled campaign'
}

export function toLatestCampaigns(campaigns: Campaign[], now = dayjs()): LatestCampaign[] {
  const cutoff = now.subtract(LATEST_CAMPAIGNS_MONTHS, 'month')
  return campaigns
    .map((campaign) => ({
      id: campaign.id,
      name: toName(campaign),
      channel: campaign.communicationChannel,
      recipients: campaign.recipientCount,
      responseRate: toPercent(campaign.responseRate),
      clickRate: toPercent(campaign.linkClickRate),
      stopRate: toPercent(campaign.stopRate),
      status: CAMPAIGN_STATUS_DISPLAY[campaign.displayStatus] ?? { label: campaign.displayStatus, tone: 'neutral' },
      ...toDate(campaign),
    }))
    .filter((campaign) => !dayjs(campaign.date).isBefore(cutoff))
    .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf())
    .slice(0, LATEST_CAMPAIGNS_LIMIT)
}
